/**
 * Pure orbital mechanics for the "Time" chapter: Kepler's equation, positions on an ellipse with the
 * hole at one focus, the exaggerated relativistic turn of the orbit, and the S-star cluster.
 * No three.js and no DOM, so all of it is tested in node.
 *
 * Scene units: 100 astronomical units per unit. Angles are radians unless a name says Deg.
 */

export const TWO_PI = Math.PI * 2;
export const DEG = Math.PI / 180;

/* ---- S2 ---- */

/** semi-major axis: 1,030 AU */
export const S2_A = 10.3;
export const S2_E = 0.885;
/** seconds per orbit in the scene (the real star takes about 16 years) */
export const S2_PERIOD = 16;
/** closest approach, in scene units */
export const S2_PERICENTRE = S2_A * (1 - S2_E);
/** the scene opens with S2 at apocentre */
export const S2_START_MEAN_ANOMALY = Math.PI;

/* ---- precession ---- */

/** the real general-relativistic advance of S2's pericentre, per orbit */
export const PRECESSION_REAL_ARCMIN = 12;
/** how much larger the picture draws it */
export const PRECESSION_EXAGGERATION = 200;
/** 12 arcminutes x 200 = 40 degrees per orbit */
export const PRECESSION_VISIBLE_DEG = (PRECESSION_REAL_ARCMIN * PRECESSION_EXAGGERATION) / 60;
export const PRECESSION_PER_ORBIT = PRECESSION_VISIBLE_DEG * DEG;

/** 1 solar mass of GM/c^2, in km */
export const SUN_GM_C2_KM = 1.4766250385;
export const AU_KM = 149597870.7;
/** Sagittarius A*: about 4.3 million solar masses */
export const SGR_A_GM_C2_KM = 4.3e6 * SUN_GM_C2_KM;
export const S2_A_AU = S2_A * 100;

/** Real general-relativistic advance of the pericentre per orbit, radians: 6 pi GM / (c^2 a (1 - e^2)). */
export function grPrecessionPerOrbit(gmOverC2: number, a: number, e: number): number {
  return (6 * Math.PI * gmOverC2) / (a * (1 - e * e));
}

/**
 * Argument of periapsis after a given total mean anomaly, with the exaggerated turn applied continuously:
 * exactly PRECESSION_PER_ORBIT (40 degrees) after 2 pi of mean anomaly.
 */
export function argPeriapsisAt(meanAnomalyTotal: number, omega0 = 0): number {
  return omega0 + (PRECESSION_PER_ORBIT * meanAnomalyTotal) / TWO_PI;
}

/* ---- Kepler ---- */

/**
 * Solve Kepler's equation E - e sin E = M for the eccentric anomaly. Works for any real M (the result is on the
 * same branch, so the identity holds for the M you passed in) and for 0 <= e < 1, including e = 0.885.
 * Safeguarded Newton on the monotone interval [0, pi], so it cannot diverge at high eccentricity.
 */
export function solveKepler(M: number, e: number): number {
  const k = Math.round(M / TWO_PI);
  let m = M - k * TWO_PI; // [-pi, pi]
  const sign = m < 0 ? -1 : 1;
  m = Math.abs(m);
  let lo = 0, hi = Math.PI;
  let E = e > 0.8 ? Math.PI : m + e * Math.sin(m);
  E = Math.min(hi, Math.max(lo, E));
  for (let i = 0; i < 60; i++) {
    const f = E - e * Math.sin(E) - m;
    if (f > 0) hi = E; else lo = E;
    const df = 1 - e * Math.cos(E);
    let next = E - f / df;
    if (!(next > lo && next < hi)) next = 0.5 * (lo + hi);
    const done = Math.abs(next - E) < 1e-15;
    E = next;
    if (done) break;
  }
  return sign * E + k * TWO_PI;
}

export interface OrbitPoint { x: number; y: number; r: number; }

/**
 * Position in the orbit plane, relative to the focus (the hole), for a mean anomaly. Pericentre is at M = 0
 * on the +x axis when argPeriapsis = 0; the motion is counter-clockwise.
 */
export function positionAt(meanAnomaly: number, a: number, e: number, argPeriapsis: number): OrbitPoint {
  const E = solveKepler(meanAnomaly, e);
  const cE = Math.cos(E), sE = Math.sin(E);
  const xp = a * (cE - e);
  const yp = a * Math.sqrt(1 - e * e) * sE;
  const c = Math.cos(argPeriapsis), s = Math.sin(argPeriapsis);
  return { x: xp * c - yp * s, y: xp * s + yp * c, r: a * (1 - e * cE) };
}

/** Mean anomaly after t seconds on an orbit with the given period. */
export function meanAnomalyAt(t: number, period: number, m0 = 0): number {
  return m0 + (TWO_PI * t) / period;
}

/**
 * S2 for a cumulative mean anomaly `phase` measured from the start of the scene (apocentre).
 * The ellipse turns as it goes, so the argument of periapsis is a function of the same number.
 */
export function s2At(phase: number): OrbitPoint & { omega: number } {
  const omega = argPeriapsisAt(phase);
  const p = positionAt(S2_START_MEAN_ANOMALY + phase, S2_A, S2_E, omega);
  return { ...p, omega };
}

/** The phase at which S2 makes its k-th pericentre passage (k = 1 is the first). */
export function pericentrePhase(k: number): number {
  return TWO_PI * (k - 1) + (TWO_PI - S2_START_MEAN_ANOMALY);
}

/** Direction, in radians, of S2's k-th pericentre passage. Successive passages differ by exactly one turn of the orbit. */
export function pericentreDirection(k: number): number {
  return argPeriapsisAt(pericentrePhase(k));
}

/** The phase at which the first full lap is done: back at apocentre. */
export const FIRST_LAP_PHASE = TWO_PI;

/* ---- geometry helpers ---- */

/** Wrap an angle to [-pi, pi). */
export function wrapAngle(a: number): number {
  return ((((a + Math.PI) % TWO_PI) + TWO_PI) % TWO_PI) - Math.PI;
}

/** Angle in degrees, 0..180, between the direction of the point (x, y) from the origin and a direction in radians. */
export function angularErrorDeg(x: number, y: number, directionRad: number): number {
  return Math.abs(wrapAngle(Math.atan2(y, x) - directionRad)) / DEG;
}

/**
 * Fill `out` (xyz triples, z = 0) with n points around the ellipse, sampled evenly in eccentric anomaly,
 * rotated by argPeriapsis. Returns `out`.
 */
export function sampleOrbit(n: number, a: number, e: number, argPeriapsis: number, out: Float32Array = new Float32Array(n * 3)): Float32Array {
  const b = a * Math.sqrt(1 - e * e), c = Math.cos(argPeriapsis), s = Math.sin(argPeriapsis);
  for (let i = 0; i < n; i++) {
    const u = (TWO_PI * i) / n;
    const xp = a * (Math.cos(u) - e), yp = b * Math.sin(u);
    out[i * 3] = xp * c - yp * s;
    out[i * 3 + 1] = xp * s + yp * c;
    out[i * 3 + 2] = 0;
  }
  return out;
}

/* ---- seeded randomness and the S-star cluster ---- */

/** Small fast seeded generator, returns a function yielding [0, 1). */
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface SStar {
  a: number;
  e: number;
  /** argument of periapsis */
  omega: number;
  /** tilt of the orbit plane about the x axis, radians */
  tilt: number;
  /** seconds per orbit */
  period: number;
  /** mean anomaly at t = 0 */
  m0: number;
}

export const S_STAR_MAX_TILT = 35 * DEG;

/** The faint cluster around S2: each star on its own ellipse. Same seed, same sky. */
export function makeSStars(seed = 2002, count = 10): SStar[] {
  const rnd = mulberry32(seed);
  const stars: SStar[] = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      a: 5 + rnd() * 20,
      e: 0.3 + rnd() * 0.6,
      omega: rnd() * TWO_PI,
      tilt: (rnd() * 2 - 1) * S_STAR_MAX_TILT,
      period: 20 + rnd() * 40,
      m0: rnd() * TWO_PI
    });
  }
  return stars;
}

/** 3-D position of an S-star after t seconds: Kepler in its own plane, then tilted about the x axis. */
export function sStarPosition(s: SStar, t: number): { x: number; y: number; z: number; r: number } {
  const p = positionAt(meanAnomalyAt(t, s.period, s.m0), s.a, s.e, s.omega);
  const ct = Math.cos(s.tilt), st = Math.sin(s.tilt);
  return { x: p.x, y: p.y * ct, z: p.y * st, r: p.r };
}
